import {useRouter} from 'next/router';
import PropTypes from 'prop-types';
import {memo, useCallback, useEffect, useRef} from 'react';
import {useSelector} from 'react-redux';

import {RESPONSE_STATUSES} from '@/src/app/constants/api';
import {
  ORDER_BY_FILTER,
  ORDER_FILTER,
  RESULT_DATA_TYPE,
  SEARCH_FILTERS,
  SEARCH_TYPE,
} from '@/src/app/constants/search';
import {selectResults} from '@/src/app/store/search/searchSlice';
import useSearchActions from '@/src/app/store/search/useSearchActions';
import transforCoursesData from '@/src/app/utils/transforCoursesData';
import {transformClinicDataForCard} from '@/src/app/utils/transformClinicData';
import getTransformedPosts from '@/src/app/utils/transformedPosts';
import transformUserData from '@/src/app/utils/transformUserData';
import useIsMobile from '@/src/hooks/useIsMobile';
import useLoading from '@/src/hooks/useLoading';
import useRoutePaths from '@/src/hooks/useRoutePaths';
import SearchTabsNavigation from '@/src/Search/components/SearchTabsNavigation';
import TotalResultsCount from '@/src/Search/components/TotalResultsCount';

import {LoadingWrapper} from '../../ui';
import FiltersLayout from '../components/FiltersLayout';
import css from '../styles/SearchContainer.module.css';
import {checkExsistingFilter, getData, getResultDataType} from '../utils';
import {useSearchFilters} from '../utils/useSearchFilters';
import SearchFieldContainer from './SearchFieldContainer';
import SearchResults from './SearchResults';
import SearchTabsContainer from './SearchTabsContainer';

export const getIsQueryParamsExist = (query) => {
  //eslint-disable-next-line
  const {slug, username, ...exsistingFilter} = query;

  return Object.keys(exsistingFilter).length > 0;
};

const SearchContainer = ({
  targetUserId,
  currentUserId,
  onFollowToggleCallback,
  userType,
}) => {
  const {isLoading, setLoading} = useLoading();
  const {query, locale, push, isReady: isRouterReady} = useRouter();
  const {setResultsDataAction} = useSearchActions();
  const isQueryParamsExist = getIsQueryParamsExist(query);
  const {setQueryFilters, isTargetUserPage, isSearchPage, isWatchlistPage} =
    useSearchFilters(userType);
  const {
    SEARCH_COURSES,
    SEARCH_USERS,
    SEARCH_UNITED_ROUTE,
    SEARCH_CLINICS,
    SEARCH_VACANCIES,
    SEARCH_POSTS,
  } = useRoutePaths();
  const areInitialFiltersSetRef = useRef(false);
  const {totalAmount} = useSelector(selectResults);
  const {isMaxWidth900} = useIsMobile();
  const controllerRef = useRef();

  const initialSearchState = {
    [SEARCH_FILTERS.OFFSET]: query[SEARCH_FILTERS.OFFSET] || '0',
    [SEARCH_FILTERS.ORDER]: query[SEARCH_FILTERS.ORDER] || ORDER_FILTER.DESC,
    [SEARCH_FILTERS.ORDER_BY]: ORDER_BY_FILTER.APPROVED_AT,
  };

  useEffect(() => {
    if (
      (isRouterReady && !areInitialFiltersSetRef.current) ||
      locale !== query[SEARCH_FILTERS.LANGUAGE]
    ) {
      setQueryFilters({
        ...query,
        [SEARCH_FILTERS.LANGUAGE]: locale,
        ...initialSearchState,
      });
      areInitialFiltersSetRef.current = true;
    }

    return () => setResultsDataAction({});
  }, [isRouterReady, locale, query[SEARCH_FILTERS.LANGUAGE]]);

  useEffect(() => {
    if (controllerRef.current) {
      // @ts-ignore
      controllerRef.current.abort();
    }

    if (locale === query[SEARCH_FILTERS.LANGUAGE]) {
      // @ts-ignore
      controllerRef.current = new AbortController();

      requestGetData(query, query.slug);
    }
  }, [
    query.slug,
    query.search,
    JSON.stringify(checkExsistingFilter(query)),
    locale,
  ]);

  const requestGetData = useCallback(
    async (params, slug) => {
      setLoading(true);

      const {status, responseBody} = await getData(
        params,
        slug,
        targetUserId,
        currentUserId,
        // @ts-ignore
        controllerRef.current?.signal
      );
      setLoading(false);

      if (status === RESPONSE_STATUSES.SUCCESS) {
        getResultDataType(slug) === RESULT_DATA_TYPE.POSTS &&
          setResultsDataAction({
            data: getTransformedPosts(responseBody?.data || []),
            totalAmount: responseBody.total_amount,
          });

        getResultDataType(slug) === RESULT_DATA_TYPE.COURSE &&
          setResultsDataAction({
            data: transforCoursesData(
              responseBody?.data || [],
              locale,
              query[SEARCH_FILTERS.LANGUAGES]
            ),
            totalAmount: responseBody.total_amount,
          });

        (getResultDataType(slug) === RESULT_DATA_TYPE.USER ||
          getResultDataType(slug) === RESULT_DATA_TYPE.FOLLOW) &&
          setResultsDataAction({
            data: responseBody.data.map((user) => transformUserData(user)),
            totalAmount: responseBody.total_amount,
          });

        getResultDataType(slug) === RESULT_DATA_TYPE.CLINICS &&
          setResultsDataAction({
            data: responseBody.data.map((clinic) =>
              transformClinicDataForCard(clinic, locale)
            ),
            totalAmount: responseBody.total_amount,
          });
      }

      controllerRef.current = null;
    },
    [query, targetUserId]
  );

  const handleTabClick = useCallback(
    (newSearchType) => {
      let newPathname = null;

      setResultsDataAction({});

      switch (newSearchType) {
        case SEARCH_TYPE.USERS: {
          newPathname = SEARCH_USERS.getLink();
          break;
        }
        case SEARCH_TYPE.COURSES: {
          newPathname = SEARCH_COURSES.getLink();
          break;
        }
        case SEARCH_TYPE.POSTS: {
          newPathname = SEARCH_POSTS.getLink();
          break;
        }
        case SEARCH_TYPE.CLINICS: {
          newPathname = SEARCH_CLINICS.getLink();
          break;
        }
        case SEARCH_TYPE.UNITED: {
          newPathname = SEARCH_UNITED_ROUTE.getLink();
          break;
        }
        case SEARCH_TYPE.VACANCIES: {
          newPathname = SEARCH_VACANCIES.getLink();
        }
      }

      push({
        pathname: newPathname,
        query: {
          [SEARCH_FILTERS.OFFSET]: '0',
          [SEARCH_FILTERS.ORDER]: ORDER_FILTER.DESC,
          [SEARCH_FILTERS.LANGUAGE]: locale,
          ...(query[SEARCH_FILTERS.SEARCH]
            ? {
                [SEARCH_FILTERS.SEARCH]: query[SEARCH_FILTERS.SEARCH],
              }
            : {}),
        },
      });
    },
    [query, locale]
  );

  return (
    <>
      {(isTargetUserPage || isWatchlistPage) && (
        <SearchTabsContainer userType={userType} />
      )}
      {isSearchPage && <SearchFieldContainer />}
      {isSearchPage && (
        <SearchTabsNavigation
          onTabClick={handleTabClick}
          // @ts-ignore
          activeSearchType={query.slug}
        />
      )}
      <LoadingWrapper isLoading={isLoading}>
        {isQueryParamsExist && (
          <div className={css.contentWrapper}>
            {isSearchPage && !isLoading && !isMaxWidth900 && (
              <TotalResultsCount
                // @ts-ignore
                activeSearchType={query.slug}
                totalAmount={totalAmount}
                // @ts-ignore
                searchQuery={query[SEARCH_FILTERS.SEARCH]}
              />
            )}
            <div className={css.content}>
              <FiltersLayout userType={userType} />
              {isMaxWidth900 && (
                <TotalResultsCount
                  // @ts-ignore
                  activeSearchType={query.slug}
                  totalAmount={totalAmount}
                  // @ts-ignore
                  searchQuery={query[SEARCH_FILTERS.SEARCH]}
                />
              )}
              <section className={css.section}>
                <SearchResults
                  onFollowToggleCallback={onFollowToggleCallback}
                  currentUserId={currentUserId}
                  userType={userType}
                />
              </section>
            </div>
          </div>
        )}
      </LoadingWrapper>
    </>
  );
};

SearchContainer.propTypes = {
  targetUserId: PropTypes.number,
  currentUserId: PropTypes.number,
  onFollowToggleCallback: PropTypes.func,
  userType: PropTypes.string,
};

SearchContainer.defaultProps = {
  targetUserId: null,
  currentUserId: null,
  onFollowToggleCallback: null,
};

export default memo(SearchContainer);
